import { useState } from 'react';
import './PartImageGrid.css';

interface PartImageGridProps {
  label: string;
  // Thumbnail folder under /builder-thumbs (matches the PartSlot key, or 'outfit')
  slot: string;
  // Current value. With zeroLabel set the range is 0..count (0 = auto/none);
  // without it real styles run startIndex..startIndex+count-1.
  value: number;
  count: number;
  // Tile for value 0 (e.g. 'Auto' or 'None'). Omit when 0 is a real style.
  zeroLabel?: string;
  // First real style index when there is no zero tile (hair = 0, outfit = 1).
  startIndex?: number;
  // Optional per-index tile captions (indexed by style value, e.g. outfit names).
  tileLabels?: string[];
  // Bigger tiles for full-body shots (outfits).
  large?: boolean;
  onChange: (value: number) => void;
}

export function PartImageGrid({
  label,
  slot,
  value,
  count,
  zeroLabel,
  startIndex = 1,
  tileLabels,
  large,
  onChange,
}: PartImageGridProps) {
  // Indices whose thumbnail failed to load render as a numbered tile instead.
  const [failed, setFailed] = useState<Set<number>>(() => new Set());

  const first = zeroLabel ? 1 : startIndex;
  const styleIndices = Array.from({ length: count }, (_, i) => first + i);

  const markFailed = (idx: number) =>
    setFailed((prev) => {
      const next = new Set(prev);
      next.add(idx);
      return next;
    });

  return (
    <div className="part-image-grid">
      <label className="control-label">{label}</label>
      <div className={`pig-tiles ${large ? 'pig-tiles-large' : ''}`}>
        {zeroLabel && (
          <button
            type="button"
            className={`pig-tile pig-tile-none ${value === 0 ? 'active' : ''}`}
            onClick={() => onChange(0)}
            aria-label={`${label}: ${zeroLabel}`}
          >
            <span className="pig-none-mark" aria-hidden="true">&#8709;</span>
            <span className="pig-none-label">{zeroLabel}</span>
          </button>
        )}
        {styleIndices.map((idx) => (
          <button
            key={idx}
            type="button"
            className={`pig-tile ${value === idx ? 'active' : ''}`}
            onClick={() => onChange(idx)}
            aria-label={`${label} ${tileLabels?.[idx] ?? idx}`}
          >
            {failed.has(idx) ? (
              <span className="pig-fallback">{tileLabels?.[idx] ?? `#${idx}`}</span>
            ) : (
              <img
                className="pig-img"
                src={`/builder-thumbs/${slot}/${idx}.png`}
                alt=""
                loading="lazy"
                draggable={false}
                onError={() => markFailed(idx)}
              />
            )}
            {tileLabels?.[idx] && !failed.has(idx) && (
              <span className="pig-tile-label">{tileLabels[idx]}</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

export default PartImageGrid;
